<template>
  <v-app>
    <!-- ============================================================
         CUSTOMER DASHBOARD SIDEBAR
    ============================================================= -->
    <v-navigation-drawer
      v-model="drawer"
      :temporary="!lgAndUp"
      :permanent="lgAndUp"
      :width="sidebarWidth"
      class="customer-dashboard-drawer"
    >
      <CustomerDashboardSidebar
        @close="closeMobileDrawer"
      />
    </v-navigation-drawer>

    <!-- ============================================================
         TOP BAR
    ============================================================= -->
    <v-app-bar
      flat
      border
      height="74"
      class="customer-dashboard-app-bar"
      :style="{
        background: themeStore.themeStyles.background,
        color: themeStore.themeStyles.color
      }"
    >
      <!-- MOBILE MENU -->
      <v-btn
        v-if="!lgAndUp"
        icon
        variant="text"
        class="mr-1"
        @click="drawer = !drawer"
      >
        <v-icon>
          mdi-menu
        </v-icon>
      </v-btn>

      <!-- ========================================================
           GLOBAL SEARCH
      ========================================================= -->
      <v-text-field
        v-if="mdAndUp"
        v-model="search"
        placeholder="Search anything..."
        prepend-inner-icon="mdi-magnify"
        variant="solo-filled"
        density="comfortable"
        rounded="lg"
        hide-details
        class="dashboard-search"
        @keyup.enter="handleSearch"
      >
        <template #append-inner>
          <div class="search-shortcut">
            ⌘ K
          </div>
        </template>
      </v-text-field>

      <v-spacer />

      <!-- ========================================================
           HOSTING DASHBOARD - ADMIN ONLY
      ========================================================= -->
      <v-btn
        v-if="isAdmin"
        color="primary"
        variant="tonal"
        prepend-icon="mdi-server-outline"
        class="text-none mr-2 hosting-dashboard-button"
        @click="goToHostingDashboard"
      >
        <span v-if="smAndUp">
          Hosting Dashboard
        </span>
      </v-btn>

      <!-- ========================================================
           NOTIFICATIONS
      ========================================================= -->
      <v-menu
        location="bottom end"
        :close-on-content-click="false"
      >
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            icon
            variant="text"
            class="mr-1"
          >
            <v-badge
              v-if="notificationCount > 0"
              :content="notificationCount"
              color="error"
              floating
            >
              <v-icon>
                mdi-bell-outline
              </v-icon>
            </v-badge>

            <v-icon v-else>
              mdi-bell-outline
            </v-icon>
          </v-btn>
        </template>

        <v-card
          width="380"
          max-width="calc(100vw - 32px)"
          rounded="xl"
        >
          <v-card-title
            class="d-flex align-center pa-5"
          >
            <div>
              <div class="font-weight-bold">
                Notifications
              </div>

              <div
                class="text-caption text-medium-emphasis"
              >
                Recent account updates
              </div>
            </div>

            <v-spacer />

            <v-chip
              size="small"
              color="primary"
              variant="tonal"
            >
              {{ notificationCount }}
            </v-chip>
          </v-card-title>

          <v-divider />

          <v-list
            v-if="notifications.length"
            density="comfortable"
          >
            <v-list-item
              v-for="notification in notifications"
              :key="notification.id"
            >
              <template #prepend>
                <v-avatar
                  :color="notification.color"
                  variant="tonal"
                  size="40"
                >
                  <v-icon size="20">
                    {{ notification.icon }}
                  </v-icon>
                </v-avatar>
              </template>

              <v-list-item-title>
                {{ notification.title }}
              </v-list-item-title>

              <v-list-item-subtitle>
                {{ notification.message }}
              </v-list-item-subtitle>
            </v-list-item>
          </v-list>

          <div
            v-else
            class="text-center pa-8 text-medium-emphasis"
          >
            <v-icon
              size="42"
              class="mb-3"
            >
              mdi-bell-check-outline
            </v-icon>

            <div>
              You're all caught up.
            </div>
          </div>
        </v-card>
      </v-menu>

      <!-- ========================================================
           LANGUAGE
      ========================================================= -->
      <div class="language-wrapper">
        <LanguageSwitcher
          @change="onLanguageChange"
        />
      </div>

      <!-- ========================================================
           USER MENU
      ========================================================= -->
      <v-menu location="bottom end">
        <template #activator="{ props }">
          <v-btn
            v-bind="props"
            variant="text"
            class="customer-account-button text-none"
          >
            <div
              v-if="mdAndUp"
              class="customer-account-copy"
            >
              <div class="customer-account-name">
                {{ customerName }}
              </div>

              <div class="customer-account-role">
                Customer
              </div>
            </div>

            <v-avatar
              color="primary"
              size="40"
              class="ml-2"
            >
              <v-img
                v-if="customerAvatar"
                :src="customerAvatar"
                cover
              />

              <span
                v-else
                class="font-weight-bold"
              >
                {{ customerInitials }}
              </span>
            </v-avatar>

            <v-icon
              v-if="mdAndUp"
              size="18"
              class="ml-1"
            >
              mdi-chevron-down
            </v-icon>
          </v-btn>
        </template>

        <v-list min-width="240">
          <v-list-item
            prepend-icon="mdi-account-outline"
            :to="{
              name: 'CustomerProfile'
            }"
          >
            My Profile
          </v-list-item>

          <v-list-item
            prepend-icon="mdi-cog-outline"
            :to="{
              name: 'CustomerSettings'
            }"
          >
            Settings
          </v-list-item>

          <v-divider />

          <v-list-item
            prepend-icon="mdi-home-outline"
            @click="goToWebsite"
          >
            Back to Website
          </v-list-item>

          <v-list-item
            prepend-icon="mdi-logout"
            base-color="error"
            @click="handleLogout"
          >
            Sign Out
          </v-list-item>
        </v-list>
      </v-menu>
    </v-app-bar>

    <!-- ============================================================
         MAIN CONTENT
    ============================================================= -->
    <v-main class="customer-dashboard-main">
      <main class="customer-dashboard-content">
        <router-view />
      </main>

      <CustomerDashboardFooter />

      <!-- ========================================================
           SUPPORT BUTTON
      ========================================================= -->
      <v-btn
        icon
        size="large"
        color="primary"
        elevation="6"
        class="support-floating-button"
        @click="openSupport"
      >
        <v-icon size="28">
          mdi-headset
        </v-icon>
      </v-btn>
    </v-main>
  </v-app>
</template>

<script setup>
import {
  computed,
  onMounted,
  ref
} from "vue"

import {
  useRouter
} from "vue-router"

import {
  useDisplay
} from "vuetify"

import {
  useAuthStore,
  useThemeStore
} from "@/store/auth"

import {
  useTenantStore
} from "@/store/tenantStore.js"

import CustomerDashboardSidebar
  from "../components/CustomerDashboardSidebar.vue"

import CustomerDashboardFooter
  from "../components/CustomerDashboardFooter.vue"

import LanguageSwitcher
  from "@/components/LanguageSwitcher.vue"

// ============================================================
// STORES
// ============================================================

const auth =
  useAuthStore()

const themeStore =
  useThemeStore()

const tenantStore =
  useTenantStore()

const router =
  useRouter()

const {
  smAndUp,
  mdAndUp,
  lgAndUp
} = useDisplay()

// ============================================================
// DRAWER
// ============================================================

const drawer =
  ref(true)

const sidebarWidth =
  258

function closeMobileDrawer() {
  if (!lgAndUp.value) {
    drawer.value =
      false
  }
}

// ============================================================
// SEARCH
// ============================================================

const search =
  ref("")

function handleSearch() {
  const value =
    search.value.trim()

  if (!value) {
    return
  }

  router.push({
    name: "CustomerOrders",
    query: {
      search: value
    }
  })
}

// ============================================================
// ADMIN
// ============================================================

const rolesArray =
  computed(() => {
    const roles =
      auth.roles

    if (!roles) {
      return []
    }

    if (Array.isArray(roles)) {
      return roles
    }

    try {
      const parsed =
        JSON.parse(roles)

      return Array.isArray(parsed)
        ? parsed
        : [parsed]
    }
    catch {
      return [roles]
    }
  })

const isAdmin =
  computed(() => {
    return (
      rolesArray.value.includes(
        "SuperUser"
      ) ||
      rolesArray.value.includes(
        "Admin"
      )
    )
  })

function goToHostingDashboard() {
  router.push({
    name: "HostingDashboard"
  })
}

// ============================================================
// CUSTOMER
// ============================================================

const currentUser =
  computed(() =>
    auth.user ||
    auth.currentUser ||
    {}
  )

const customerName =
  computed(() => {
    const user =
      currentUser.value

    const firstName =
      user.firstName ||
      user.first_name ||
      ""

    const lastName =
      user.lastName ||
      user.last_name ||
      ""

    const fullName =
      `${firstName} ${lastName}`
        .trim()

    return (
      fullName ||
      user.name ||
      user.email ||
      "Customer"
    )
  })

const customerInitials =
  computed(() => {
    const words =
      customerName.value
        .split(" ")
        .filter(Boolean)

    if (!words.length) {
      return "C"
    }

    if (words.length === 1) {
      return words[0]
        .charAt(0)
        .toUpperCase()
    }

    return (
      words[0].charAt(0) +
      words[
        words.length - 1
      ].charAt(0)
    ).toUpperCase()
  })

const customerAvatar =
  computed(() => {
    const user =
      currentUser.value

    return (
      user.avatarUrl ||
      user.avatar ||
      user.image ||
      null
    )
  })

// ============================================================
// NOTIFICATIONS
// ============================================================

const notifications =
  ref([])

const notificationCount =
  computed(() =>
    notifications.value.length
  )

// ============================================================
// ACTIONS
// ============================================================

function onLanguageChange() {
  // LanguageSwitcher already owns language changes.
}

function goToWebsite() {
  router.push({
    name: "PublicHome"
  })
}

async function handleLogout() {
  await auth.logout()

  await router.push({
    name: "PublicHome"
  })
}

function openSupport() {
  /*
   * Hook this into Messaging, Live Chat,
   * SignalR support or your support module later.
   */
}

// ============================================================
// INITIALIZE
// ============================================================

onMounted(
  async () => {
    await tenantStore.loadTenant()
  }
)
</script>

<style scoped>
.customer-dashboard-drawer {
  border-right:
    1px solid
    rgba(
      var(--v-border-color),
      0.12
    );
}

.customer-dashboard-app-bar {
  padding-left:
    24px;

  padding-right:
    22px;
}

.dashboard-search {
  width:
    365px;

  max-width:
    365px;
}

.dashboard-search :deep(.v-field) {
  box-shadow:
    none !important;
}

.search-shortcut {
  border:
    1px solid
    rgba(
      var(--v-border-color),
      0.15
    );

  border-radius:
    6px;

  padding:
    2px 7px;

  font-size:
    11px;

  color:
    rgb(
      var(--v-theme-on-surface)
    );

  opacity:
    0.55;
}

.language-wrapper {
  display:
    flex;

  align-items:
    center;

  margin-left:
    2px;
}

.customer-account-button {
  min-width:
    0;
}

.customer-account-copy {
  text-align:
    right;

  line-height:
    1.15;
}

.customer-account-name {
  font-size:
    13px;

  font-weight:
    700;
}

.customer-account-role {
  margin-top:
    3px;

  font-size:
    11px;

  opacity:
    0.65;
}

.customer-dashboard-main {
  min-height:
    100vh;

  background:
    #f8f9fd;
}

.customer-dashboard-content {
  width:
    100%;

  max-width:
    1700px;

  min-height:
    calc(
      100vh - 130px
    );

  margin:
    0 auto;

  padding:
    24px 30px 30px;
}

.support-floating-button {
  position:
    fixed;

  right:
    30px;

  bottom:
    82px;

  z-index:
    1000;
}

@media (
  max-width: 1279px
) {
  .customer-dashboard-app-bar {
    padding-left:
      10px;

    padding-right:
      10px;
  }

  .customer-dashboard-content {
    padding:
      20px;
  }
}

@media (
  max-width: 959px
) {
  .customer-dashboard-content {
    padding:
      16px;
  }

  .support-floating-button {
    right:
      18px;

    bottom:
      72px;
  }
}

@media (
  max-width: 599px
) {
  .hosting-dashboard-button {
    min-width:
      42px;
  }

  .customer-dashboard-content {
    padding:
      12px;
  }
}
</style>